import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [stage, setStage] = useState("email"); // email | reset
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const sendOtp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/otp/request", { identifier: email, channel: "email", purpose: "password_reset" });
      toast.success("If an account exists for that email, a reset code has been sent.");
      setStage("reset");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send reset code");
    } finally {
      setSubmitting(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/password/reset", { identifier: email, otp: otp.trim(), new_password: password });
      toast.success("Password reset — please sign in.");
      nav("/login", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 bg-[#FEF3E2]/40">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-[#1a3c5e]/10 p-8">
        <h1 className="font-display text-2xl font-bold text-[#1a3c5e]">Reset your password</h1>
        <p className="text-sm text-gray-500 mt-1">
          {stage === "email" ? "Enter your account email and we'll send a reset code." : `Code sent to ${email}.`}
        </p>

        {stage === "email" ? (
          <form onSubmit={sendOtp} className="space-y-4 mt-6">
            <div>
              <label className="text-xs font-semibold text-[#1a3c5e] uppercase tracking-wider">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" className="w-full mt-1 border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
            </div>
            <button disabled={submitting} type="submit" data-testid="forgot-send" className="w-full py-3 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-60 text-white font-semibold rounded-xl shadow-md transition flex items-center justify-center gap-2">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Send Reset Code
            </button>
          </form>
        ) : (
          <form onSubmit={resetPassword} className="space-y-4 mt-6">
            <p className="text-sm">
              <button type="button" onClick={() => setStage("email")} className="text-[#F7941D] font-semibold hover:underline">Use a different email</button>
            </p>
            <div>
              <label className="text-xs font-semibold text-[#1a3c5e] uppercase tracking-wider">Reset Code</label>
              <input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required maxLength={6} data-testid="forgot-otp"
                className="w-full mt-1 border border-gray-300 rounded-xl px-4 py-3 tracking-[0.5em] text-center text-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#1a3c5e] uppercase tracking-wider">New Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="forgot-new-password" className="w-full mt-1 border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
            </div>
            <button disabled={submitting} type="submit" data-testid="forgot-reset-submit" className="w-full py-3 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-60 text-white font-semibold rounded-xl shadow-md transition flex items-center justify-center gap-2">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Reset Password
            </button>
          </form>
        )}

        <p className="text-sm text-gray-500 mt-5 text-center">
          <Link to="/login" className="text-[#F7941D] font-semibold hover:underline">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
